import { G } from '@mobily/ts-belt';
import { CreateInDialogButtonProps } from '@react-admin/ra-form-layout';
import { useCallback, useMemo } from 'react';
import { useNotify, useRefresh } from 'react-admin';

import { CreateInDialogButton, StepperForm } from '@sh4res/bo-common/components';
import {
  getSteps,
  useCampaignConfirmationDialog,
} from '@sh4res/bo-common/components/B2bCampaigns/common';
import { handleCampaignTransform } from '@sh4res/bo-common/components/B2bCampaigns/transform';
import { useUploadPresignedUrl } from '@sh4res/bo-common/hooks';
import { getErrorMessage, Resources } from '@sh4res/bo-common/utils';
import { isBoExternal } from '@sh4res/bo-common/utils/env';

export const B2bCampaignsCreate = ({
  resource,
  companyId,
  ...props
}: CreateInDialogButtonProps['ButtonProps'] & {
  resource: Resources;
  companyId?: number;
}) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const confirmation = useCampaignConfirmationDialog();
  const { upload } = useUploadPresignedUrl();

  const onError = useCallback(
    (error: unknown) => {
      notify('resources.bo_common.b2b.campaigns.actions.create.failure', {
        type: 'warning',
        messageArgs: { error: getErrorMessage(error) },
        multiLine: true,
      });
    },
    [notify],
  );

  const onSuccess = useCallback(
    (record: { id: number }) => {
      confirmation.open({
        campaignId: record.id,
        title: 'resources.bo_common.b2b.campaigns.sections.success_modal.title',
        body: 'resources.bo_common.b2b.campaigns.sections.success_modal.body',
        showAllocations: true,
      });
      refresh();
    },
    [confirmation, refresh],
  );

  const handleTransform = useCallback(
    (isDraft: boolean) => handleCampaignTransform({ isDraft, action: 'create', upload }),
    [upload],
  );

  const steps = useMemo(
    () =>
      getSteps({
        isBoExternal: isBoExternal(),
        action: 'create',
        openConfirmationDialog: confirmation.open,
      }),
    [confirmation],
  );

  return (
    <>
      <CreateInDialogButton
        resource={resource}
        fullWidth
        maxWidth="xl"
        title="resources.bo_common.b2b.campaigns.actions.create.label"
        testId="BoExternalB2bCampaignsCreate_CreateButton"
        ButtonProps={props}
        transform={handleTransform(false)}
        mutationOptions={{ onSuccess, onError }}
        disableEscapeKeyDown
        sx={{
          '& .MuiDialog-paper': {
            height: '100%',
          },
          '& form': {
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          },
        }}>
        <StepperForm
          alwaysEnableSaveButton
          steps={steps}
          defaultValues={G.isNotNullable(companyId) ? { company_id: companyId } : undefined}
          sx={{
            flexGrow: 1,
            '& > div': {
              height: '100%',
            },
          }}
          permissions={{
            resource,
            action: 'create',
          }}
          draft={{
            transform: handleTransform(true),
            resource,
            action: 'create',
          }}
          testId="BoExternalB2bCampaignsCreate"
        />
      </CreateInDialogButton>
      {confirmation.Dialog}
    </>
  );
};
